import { UiButtonDirective, UiControlDirective } from '@template-ui/directives';
import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
  signal,
  inject,
} from '@angular/core';

import {
  WorkflowNotificationContextProfile,
  WorkflowNotificationMessage,
  WorkflowNotificationVariable,
} from './platform-workflow-definition-lifecycle.models';
import {
  availableNotificationVariables,
  compatibleNotificationProfiles,
  NotificationMessageEventKind,
  NotificationMessageField,
  NotificationMessageIssue,
  NotificationMessagePreview,
  notificationMessageVariableCodes,
  previewNotificationMessage,
  resolveNotificationMessageProfile,
  validateNotificationMessage,
} from './workflow-notification-message-template';
import { WorkflowEditorPendingScope } from './workflow-editor-pending-state';
import { WorkflowOwnerViewState } from './workflow-owner-view-state';

@Component({
  selector: 'phms-workflow-notification-message-editor',
  imports: [UiButtonDirective, UiControlDirective, CommonModule],
  templateUrl: './workflow-notification-message-editor.component.html',
  styleUrl: './workflow-notification-message-editor.component.scss',
})
export class WorkflowNotificationMessageEditorComponent implements OnChanges {
  @Input() message: WorkflowNotificationMessage | null = null;
  @Input() contextProfiles: WorkflowNotificationContextProfile[] = [];
  @Input() subjectTypeCode = '';
  @Input() eventKind: NotificationMessageEventKind = 'transition';
  @Input() canAuthor = false;
  @Input() inspectionMode = false;
  @Input() interactionBlocked = false;
  @Input() referenceLoading = false;
  @Input() ownerKey = '';
  @Input() resetVersion = 0;
  @Input() instanceId = 'workflow-notification-message';

  @Output() readonly messageChange =
    new EventEmitter<WorkflowNotificationMessage | null>();
  @Output() readonly pendingChangesChange = new EventEmitter<boolean>();

  @ViewChild('editorHeading') private editorHeading?: ElementRef<HTMLElement>;
  @ViewChild('titleInput') private titleInput?: ElementRef<HTMLInputElement>;
  @ViewChild('bodyInput') private bodyInput?: ElementRef<HTMLTextAreaElement>;

  readonly formDirty = signal(false);
  readonly configured = signal(false);
  readonly removalPending = signal(false);
  readonly localError = signal<string | null>(null);
  readonly localStatus = signal<string | null>(null);
  readonly missingContextScenario = signal(false);

  contextProfileCode = '';
  contextProfileVersion = 0;
  titleTemplate = '';
  bodyTemplate = '';

  private baselineFingerprint = 'null';
  private readonly ownerViews = inject(WorkflowOwnerViewState, { optional: true });

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['ownerKey'] ||
      changes['resetVersion'] ||
      (changes['message'] && !this.formDirty())
    ) {
      this.restoreAppliedState();
    }
  }

  get canEdit(): boolean {
    return (
      this.canAuthor &&
      !this.inspectionMode &&
      !this.interactionBlocked &&
      !this.referenceLoading &&
      !this.unsupportedVersion
    );
  }

  get unsupportedVersion(): boolean {
    return this.message !== null && this.message.version !== 1;
  }

  get compatibleProfiles(): WorkflowNotificationContextProfile[] {
    return compatibleNotificationProfiles(
      this.contextProfiles,
      this.subjectTypeCode,
      this.eventKind,
    );
  }

  get draftMessage(): WorkflowNotificationMessage | null {
    if (!this.configured() || this.removalPending()) return null;
    if (this.unsupportedVersion) return this.message;
    return {
      ...this.message,
      version: 1,
      contextProfileCode: this.contextProfileCode,
      contextProfileVersion: this.contextProfileVersion,
      titleTemplate: this.titleTemplate,
      bodyTemplate: this.bodyTemplate,
    };
  }

  get selectedProfile(): WorkflowNotificationContextProfile | null {
    const message = this.draftMessage;
    return message
      ? resolveNotificationMessageProfile(
          message,
          this.contextProfiles,
          this.subjectTypeCode,
          this.eventKind,
        )
      : null;
  }

  get variables(): WorkflowNotificationVariable[] {
    return availableNotificationVariables(this.selectedProfile, this.eventKind);
  }

  get variableGroups(): Array<{
    label: string;
    variables: WorkflowNotificationVariable[];
  }> {
    const groups = new Map<string, WorkflowNotificationVariable[]>();
    for (const variable of this.variables) {
      const prefix = variable.code.split('.')[0];
      groups.set(prefix, [...(groups.get(prefix) ?? []), variable]);
    }
    const labels: Record<string, string> = {
      workflow: 'Workflow',
      step: 'Step',
      subject: 'Workflow subject',
      event: 'Event',
      status: 'Status change',
      decision: 'Decision',
      inmate: 'Inmate',
      hearing: 'Hearing schedule',
    };
    return Array.from(groups, ([code, variables]) => ({
      label: labels[code] ?? code,
      variables,
    }));
  }

  get validationIssues(): NotificationMessageIssue[] {
    return validateNotificationMessage(
      this.draftMessage,
      this.contextProfiles,
      this.subjectTypeCode,
      this.eventKind,
    );
  }

  issuesFor(field: NotificationMessageField): NotificationMessageIssue[] {
    return this.validationIssues.filter((issue) => issue.field === field);
  }

  get preview(): NotificationMessagePreview | null {
    const message = this.draftMessage;
    if (!message) return null;
    if (!this.missingContextScenario()) {
      return previewNotificationMessage(
        message,
        this.contextProfiles,
        this.subjectTypeCode,
        this.eventKind,
      );
    }
    const values = Object.fromEntries(
      this.variables.map((variable) => [variable.code, variable.sampleValue]),
    );
    const missing = this.usedVariableCodes[0];
    if (missing) delete values[missing];
    return previewNotificationMessage(
      message,
      this.contextProfiles,
      this.subjectTypeCode,
      this.eventKind,
      values,
    );
  }

  get usedVariableCodes(): string[] {
    return [
      ...new Set([
        ...notificationMessageVariableCodes(this.titleTemplate),
        ...notificationMessageVariableCodes(this.bodyTemplate),
      ]),
    ];
  }

  profileKey(profile: WorkflowNotificationContextProfile): string {
    return `${profile.code}:${profile.version}`;
  }

  get selectedProfileKey(): string {
    return `${this.contextProfileCode}:${this.contextProfileVersion}`;
  }

  beginConfigure(): void {
    if (
      !this.canEdit ||
      this.configured() ||
      this.formDirty() ||
      !this.compatibleProfiles.length
    )
      return;
    this.configured.set(true);
    this.contextProfileCode = '';
    this.contextProfileVersion = 0;
    this.titleTemplate = '';
    this.bodyTemplate = '';
    this.markPending();
    this.focusPendingChanges();
  }

  changeProfile(key: string): void {
    if (!this.canEdit || this.removalPending() || !this.configured()) return;
    const matches = this.compatibleProfiles.filter(
      (profile) => this.profileKey(profile) === key,
    );
    if (matches.length !== 1) return;
    this.contextProfileCode = matches[0].code;
    this.contextProfileVersion = matches[0].version;
    this.markPending();
  }

  changeText(field: 'title' | 'body', value: string): void {
    if (!this.canEdit || this.removalPending() || !this.configured()) return;
    if (field === 'title') this.titleTemplate = value;
    else this.bodyTemplate = value;
    this.markPending();
  }

  insertVariable(code: string, field: 'title' | 'body'): void {
    if (
      !this.canEdit ||
      this.removalPending() ||
      !this.variables.some((variable) => variable.code === code)
    )
      return;
    const control =
      field === 'title'
        ? this.titleInput?.nativeElement
        : this.bodyInput?.nativeElement;
    const value = field === 'title' ? this.titleTemplate : this.bodyTemplate;
    const start = control?.selectionStart ?? value.length;
    const end = control?.selectionEnd ?? start;
    const token = `{{${code}}}`;
    this.changeText(field, value.slice(0, start) + token + value.slice(end));
    queueMicrotask(() => {
      if (!control) return;
      control.value =
        field === 'title' ? this.titleTemplate : this.bodyTemplate;
      control.focus();
      control.setSelectionRange(start + token.length, start + token.length);
    });
  }

  stageRemoval(): void {
    if (!this.canEdit || !this.configured() || this.formDirty()) return;
    this.removalPending.set(true);
    this.markPending();
    this.focusPendingChanges();
  }

  hasPendingChanges(): boolean {
    return this.formDirty();
  }
  pendingEditorScopes(latestMessage: () => WorkflowNotificationMessage | null = () => this.message): readonly WorkflowEditorPendingScope[] {
    return [{
      id: 'notification-message', label: 'notification message input',
      state: () => {
        if (!this.formDirty()) return 'clean';
        if (this.fingerprint(latestMessage()) !== this.baselineFingerprint) return 'stale';
        if (!this.canEdit) return 'invalid';
        return validateNotificationMessage(this.removalPending() ? null : this.draftMessage, this.contextProfiles, this.subjectTypeCode, this.eventKind).length ? 'invalid' : 'valid';
      },
      fingerprint: () => JSON.stringify([this.formDirty(), this.baselineFingerprint, latestMessage(), this.ownerKey, this.resetVersion,
        this.configured(), this.removalPending(), this.contextProfileCode, this.contextProfileVersion, this.titleTemplate, this.bodyTemplate,
        this.contextProfiles, this.subjectTypeCode, this.eventKind, this.canAuthor, this.inspectionMode, this.interactionBlocked, this.referenceLoading]),
      apply: () => this.applyPendingChanges(latestMessage()),
      discard: () => { this.message = latestMessage(); this.discardPendingChanges(); },
      focus: () => this.focusPendingChanges(),
    }];
  }

  applyPendingChanges(
    latestMessage?: WorkflowNotificationMessage | null,
  ): boolean {
    if (!this.formDirty()) return true;
    if (!this.canEdit) return false;
    const latest = latestMessage === undefined ? this.message : latestMessage;
    if (this.fingerprint(latest) !== this.baselineFingerprint) {
      this.localError.set(
        "This event's message changed while you were editing. Your input remains open; discard it to load the current message before trying again.",
      );
      this.focusPendingChanges();
      return false;
    }
    const next = this.removalPending() ? null : this.draftMessage;
    if (
      validateNotificationMessage(
        next,
        this.contextProfiles,
        this.subjectTypeCode,
        this.eventKind,
      ).length
    ) {
      this.localError.set(
        'Resolve the message validation errors before applying. Your input remains open.',
      );
      this.focusPendingChanges();
      return false;
    }
    this.message = next;
    this.restoreAppliedState();
    this.localStatus.set(
      next === null
        ? 'Message removed from the local draft. Save draft persists this change.'
        : 'Message applied to the local draft. Save draft persists this change.',
    );
    this.messageChange.emit(next);
    return true;
  }

  discardPendingChanges(): void {
    this.restoreAppliedState();
  }

  focusPendingChanges(): void {
    queueMicrotask(() => { if (!this.ownerViews?.focusSuspended()) this.editorHeading?.nativeElement.focus(); });
  }

  private restoreAppliedState(): void {
    this.baselineFingerprint = this.fingerprint(this.message);
    this.configured.set(this.message !== null);
    this.removalPending.set(false);
    this.contextProfileCode = this.message?.contextProfileCode ?? '';
    this.contextProfileVersion = this.message?.contextProfileVersion ?? 0;
    this.titleTemplate =
      typeof this.message?.titleTemplate === 'string'
        ? this.message.titleTemplate
        : '';
    this.bodyTemplate =
      typeof this.message?.bodyTemplate === 'string'
        ? this.message.bodyTemplate
        : '';
    this.localError.set(null);
    this.localStatus.set(null);
    this.missingContextScenario.set(false);
    this.setPending(false);
  }

  private markPending(): void {
    this.localError.set(null);
    this.localStatus.set(null);
    this.setPending(true);
  }

  private setPending(pending: boolean): void {
    if (pending === this.formDirty()) return;
    this.formDirty.set(pending);
    this.pendingChangesChange.emit(pending);
  }

  private fingerprint(value: unknown): string {
    return JSON.stringify(value, (_key, item: unknown) => {
      if (item && typeof item === 'object' && !Array.isArray(item)) {
        return Object.fromEntries(
          Object.entries(item).sort(([a], [b]) => a.localeCompare(b)),
        );
      }
      return item;
    });
  }
}
